import { useRouter } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

import AssetCanvas from "./AssetCanvas";

import {
  DOC_FILE_EXTENSIONS,
  VIDEO_EXTENSIONS,
} from "../../../../../../utils/constants";
import { isNil } from "../../../../../../utils/fp";
import { useAppSelector } from "../../../../../../utils/hooks";
import { useTicketVersionStatusMutation } from "../../../../../../utils/queries/projects";
import { VideoMode } from "../../../../components/VideoPlayback";
import { ticketRoute } from "../../route";
import { useTicket } from "../../view";

export default function TicketCanvas() {
  const router = useRouter();
  const { ticketId } = ticketRoute.useParams();
  const { videoRef } = useTicket();

  // Slices
  const reversedVersionIndex = useAppSelector(
    (state) => state.ticket.reversedVersionIndex,
  );
  const versions = useAppSelector((state) => state.ticket.revisions);

  // Queries and mutations
  const ticketVersionStatusMutation = useTicketVersionStatusMutation(ticketId);

  // Local state
  // TODO: move all of this to the slice
  const currentVersion = useMemo(() => {
    return versions[reversedVersionIndex!];
  }, [reversedVersionIndex, versions]);

  const hasExactlyOneReviewFile = useMemo(() => {
    if (isNil(currentVersion?.reviewFiles)) {
      return false;
    }

    return currentVersion.reviewFiles.length === 1;
  }, [currentVersion]);

  const viewMode = useMemo(() => {
    if (hasExactlyOneReviewFile) {
      const firstFile = currentVersion!.reviewFiles![0];
      const { source } = firstFile;
      const fileExtension = source.slice(source.lastIndexOf(".") + 1);

      if (DOC_FILE_EXTENSIONS.includes(fileExtension.toLocaleLowerCase())) {
        return "doc";
      }

      if (VIDEO_EXTENSIONS.includes(fileExtension.toLocaleLowerCase())) {
        return "video";
      }
    }

    return "canvas";
  }, [currentVersion, hasExactlyOneReviewFile]);

  // Effect chain

  useEffect(() => {
    // When uploading a version that results in doc mode, we will have to wait
    // for some amount of processing to be done on the API. Because of this, we
    // will poll the status at some interval (3 seconds currently).
    if (
      (!currentVersion?.isPending && currentVersion?.isAIReady) ||
      isNil(currentVersion)
    ) {
      return;
    }

    const interval = setInterval(() => {
      ticketVersionStatusMutation.mutate(currentVersion._id, {
        onSuccess(data) {
          if (isNil(data)) return;

          if (
            data.isPending !== currentVersion.isPending ||
            data.isAIReady !== currentVersion.isAIReady
          ) {
            router.invalidate();
          }
        },
      });
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [currentVersion]);

  /////
  // No hooks beyond this point
  /////

  function renderDocument() {
    switch (viewMode) {
      case "video":
        // TODO: improve VideoMode to not use a ref. The component should be
        // provided functionality from the parent component pertaining to
        // comment clicks, etc. It should otherwise not try to be too smart
        // since that can lead to unnecessary bugs when things are happening
        // across many different components when comments are added.
        return <VideoMode ref={videoRef} />;
      case "doc":
      case "canvas":
        return <AssetCanvas mode={viewMode} />;
      default:
        throw new Error("Indeterminate document mode.");
    }
  }

  return renderDocument();
}
